import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  UtensilsCrossed, Cookie, Percent, Receipt, MessageSquareText, FileEdit,
  Crown, Check, ClipboardList, GraduationCap, IndianRupee, ArrowLeftRight,
  Sparkles, Flame, Clock, AlertTriangle, Zap, Download, ScrollText, Search,
  FileCheck, BookOpen, CookingPot
} from "lucide-react";
import MiniTimer from "@/components/landing/MiniTimer";

const premiumTools = [
  { icon: UtensilsCrossed, name: "PM POSHAN Daily Calculator" },
  { icon: ClipboardList, name: "PM POSHAN Monthly MDM Report Generator" },
  { icon: GraduationCap, name: "Class V & VIII Board Marks Calculator" },
  { icon: IndianRupee, name: "Salary Calculator" },
  { icon: ArrowLeftRight, name: "Unit Convertor" },
  { icon: Cookie, name: "Nutrition Ladoo Calculator" },
  { icon: Percent, name: "Marks Percentage Calculator" },
  { icon: Receipt, name: "GST Calculator" },
  { icon: MessageSquareText, name: "MDM Message Generator" },
  { icon: FileEdit, name: "Application Writer" },
  { icon: ScrollText, name: "Nutrition Ladoo Receipt" },
  { icon: CookingPot, name: "Ladoo Making Receipt" },
  { icon: FileCheck, name: "Instant CL Generator" },
  { icon: Search, name: "LO Code Finder" },
  { icon: BookOpen, name: "Daily Prayer Activities" },
  { icon: Sparkles, name: "And Many More..." },
];

const benefits = [
  "Calculate PM POSHAN / MDM in seconds",
  "Generate monthly reports and application formats instantly",
  "Board marks, salary, unit conversion — all in one place",
];

function useCountdown() {
  const getTarget = () => {
    const stored = localStorage.getItem("tut_offer_end");
    if (stored) {
      const parsed = new Date(stored).getTime();
      if (parsed > Date.now()) return parsed;
    }
    const target = Date.now() + 24 * 60 * 60 * 1000;
    localStorage.setItem("tut_offer_end", new Date(target).toISOString());
    return target;
  };

  const [target] = useState(getTarget);
  const [timeLeft, setTimeLeft] = useState({ hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    const tick = () => {
      const diff = Math.max(0, target - Date.now());
      setTimeLeft({
        hours: Math.floor(diff / 3600000),
        minutes: Math.floor((diff / 60000) % 60),
        seconds: Math.floor((diff / 1000) % 60),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [target]);

  return timeLeft;
}

function CountdownUnit({ value, label }) {
  return (
    <div className="flex flex-col items-center">
      <div className="w-14 h-14 md:w-16 md:h-16 rounded-xl bg-slate-900 flex items-center justify-center shadow-lg">
        <span className="text-2xl md:text-3xl font-bold text-white tabular-nums" style={{ fontFamily: "'Outfit', sans-serif" }}>
          {String(value).padStart(2, "0")}
        </span>
      </div>
      <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mt-1.5">{label}</span>
    </div>
  );
}

export default function PremiumToolsSection({ downloadLink }) {
  const { hours, minutes, seconds } = useCountdown();

  return (
    <section data-testid="premium-tools-section" className="py-12 md:py-20 bg-green-50">
      <div className="container mx-auto px-4 md:px-6 max-w-5xl">
        <div className="flex items-center justify-center gap-2 mb-3">
          <Crown className="w-5 h-5 text-orange-500" />
          <span className="text-orange-600 font-semibold text-sm uppercase tracking-wide">Premium</span>
        </div>
        <h2
          data-testid="premium-tools-headline"
          className="text-3xl md:text-4xl font-bold text-slate-900 text-center"
          style={{ fontFamily: "'Outfit', sans-serif" }}
        >
          Unlock Powerful Teacher Tools
        </h2>

        {/* Tools grid */}
        <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 gap-3 md:gap-4 max-w-3xl mx-auto">
          {premiumTools.map((tool, i) => (
            <Card
              key={i}
              data-testid={`premium-tool-card-${i}`}
              className={`bg-white border border-green-100 hover:border-green-300 transition-colors ${tool.name === "And Many More..." ? "col-span-2 sm:col-span-1" : ""}`}
            >
              <CardContent className="p-4 text-center">
                <div className="w-10 h-10 rounded-lg bg-orange-50 flex items-center justify-center mx-auto mb-2">
                  <tool.icon className="w-5 h-5 text-orange-500" />
                </div>
                <p className="text-sm font-medium text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  {tool.name}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Benefits */}
        <div className="mt-8 max-w-md mx-auto space-y-2">
          {benefits.map((b, i) => (
            <div key={i} className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
              <span className="text-sm text-slate-600">{b}</span>
            </div>
          ))}
        </div>

        {/* === PRICING CARD WITH PSYCHOLOGY === */}
        <Card
          data-testid="premium-pricing-card"
          className="mt-10 max-w-sm mx-auto bg-white border-2 border-orange-400 overflow-hidden shadow-2xl relative"
        >
          {/* Urgency ribbon */}
          <div className="absolute -right-8 top-5 rotate-45 bg-red-600 text-white text-[10px] font-bold uppercase tracking-wider px-10 py-1 shadow-md z-10">
            60% OFF
          </div>

          {/* Hot offer header */}
          <div className="bg-gradient-to-r from-orange-500 to-red-500 px-4 py-3 text-center">
            <div className="flex items-center justify-center gap-1.5">
              <Flame className="w-4 h-4 text-yellow-200" />
              <span className="text-white text-sm font-bold uppercase tracking-wide">
                Limited Time Offer
              </span>
              <Flame className="w-4 h-4 text-yellow-200" />
            </div>
          </div>

          <CardContent className="p-6 text-center">
            {/* Price psychology */}
            <p className="text-xs text-slate-400 uppercase tracking-wider font-medium mb-1">Annual Premium Access</p>

            <div className="flex items-center justify-center gap-3 mb-1">
              {/* Original price - crossed */}
              <div className="relative">
                <span
                  data-testid="original-price"
                  className="text-2xl font-bold text-slate-400"
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                >
                  ₹499
                </span>
                <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-red-500 -rotate-12" />
              </div>

              {/* Offer price */}
              <div className="flex items-baseline gap-0.5">
                <span
                  data-testid="offer-price"
                  className="text-5xl font-bold text-green-700"
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                >
                  ₹199
                </span>
                <span className="text-slate-500 text-sm font-medium">/ year</span>
              </div>
            </div>

            {/* Savings highlight */}
            <div className="inline-flex items-center gap-1.5 bg-green-100 text-green-700 rounded-full px-3 py-1 text-xs font-bold mb-4">
              <Zap className="w-3 h-3" />
              You Save ₹300 (60% OFF)
            </div>

            {/* Per week breakdown */}
            <p className="text-sm text-slate-500 mb-5">
              ଦିନକୁ ମାତ୍ର <strong className="text-green-700 text-base">54 ପଇସା</strong> — ଗୋଟିଏ ଚା ର ଦାମ ରୁ ମଧ୍ୟ <strong>କମ୍!</strong>
            </p>

            {/* Countdown timer */}
            <div data-testid="countdown-timer" className="bg-slate-50 rounded-xl border border-slate-200 p-4 mb-5">
              <div className="flex items-center justify-center gap-1.5 mb-3">
                <Clock className="w-4 h-4 text-red-500" />
                <p className="text-xs font-bold text-red-600 uppercase tracking-wide">
                  Offer ଶେଷ ହେବାକୁ ବାକି
                </p>
              </div>
              <div className="flex items-center justify-center gap-2 md:gap-3">
                <CountdownUnit value={hours} label="ଘଣ୍ଟା" />
                <span className="text-xl font-bold text-slate-300 self-start mt-3">:</span>
                <CountdownUnit value={minutes} label="ମିନିଟ୍" />
                <span className="text-xl font-bold text-slate-300 self-start mt-3">:</span>
                <CountdownUnit value={seconds} label="ସେକେଣ୍ଡ" />
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-5">
              <p className="text-xs text-amber-800 font-medium flex items-start gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0 mt-0.5" />
                <span>
                  ଏହି offer ସୀମିତ ସମୟ ପାଇଁ। Timer ଶେଷ ପରେ ଦାମ <strong>₹499</strong> ହୋଇଯିବ। ପରେ ଅଫସୋସ ନ ହେଉ!
                </span>
              </p>
            </div>

            {/* CTA button */}
            <Button
              data-testid="premium-download-btn"
              asChild
              className="w-full h-12 bg-green-700 hover:bg-green-800 text-white rounded-lg font-bold text-base shadow-lg shadow-green-900/20"
            >
              <a href="https://play.google.com/store/apps/details?id=com.wnapp.id1781430851106&hl=en_IN" target="_blank" rel="noopener noreferrer">
                <Download className="mr-2 w-5 h-5" />
                Google Play ରୁ Download — ₹199/year
              </a>
            </Button>

            {/* Try Online */}
            <a
              href="https://teacherutilitytoolkit.in/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="premium-try-online-btn"
              className="mt-2 w-full h-10 inline-flex items-center justify-center bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white rounded-lg font-bold text-sm shadow-md transition-all"
              onClick={() => window.gtag && window.gtag('event', 'click', { event_category: 'try_online', event_label: 'premium_try_online' })}
            >
              ⚡ Download ନ କରି ଏଠାରେ Unlock କରନ୍ତୁ!
            </a>
            <div className="mt-2 flex justify-center">
              <MiniTimer />
            </div>

            {/* Trust line */}
            <div className="mt-3 flex flex-col items-center gap-1">
              <div className="flex items-center gap-1 text-xs text-slate-400">
                <Check className="w-3 h-3 text-green-500" />
                <span>Secure Payment</span>
                <span className="mx-1">|</span>
                <Check className="w-3 h-3 text-green-500" />
                <span>Instant Access</span>
              </div>
              <p className="text-[10px] text-slate-400">
                1,000+ ଶିକ୍ଷକ ଏହି offer ଟା ନେଇ ସାରିଲେଣି
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
}
